/**
 * The decisions behind what happens when a new message arrives over the socket: which of the follow-up
 * actions apply, given what the user is looking at. Plain functions, no React and no DOM, so they can be
 * tested exhaustively. Moved out of ChatLayout's `onNew` handler with the same conditions.
 *
 * NOTE on ids: the original conditions compare the event's channel id to the open channel in two ways,
 * and both are kept exactly: some compare the RAW ids (`channelId === currentChannelId`), others compare
 * the QUALIFIED forms (`channelId === fq(currentVoidId, currentChannelId)`). They agree whenever the open
 * channel id is already qualified ("space:channel"); they differ if it is a short id.
 */

// Module copy of ChatLayout's pure key helper (same pattern as useFavorites / useAndroidShare).
const fq = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);

export type IncomingSituation = {
  /** The space and channel the message belongs to, as sent by the server. */
  voidId: string;
  channelId: string;
  /** The space and channel on screen. */
  currentVoidId: string;
  currentChannelId: string;
  /** Present when this delivery is the echo of a message this client sent. */
  tempId?: string;
  /** Whether the browser tab is visible. */
  isVisible: boolean;
  /** Pinned channels ("space:channel" ids). */
  favorites: string[];
};

export type IncomingPlan = {
  /** The message is for the open channel, comparing the raw ids. */
  isCurrentChannel: boolean;
  /** Leave search "context mode" (a live message arrived in the open channel). */
  exitContextMode: boolean;
  /** Remove this entry from the offline queue (the server has acknowledged it), or null. */
  clearOutboxFor: string | null;
  /** Mark the channel read up to this message. */
  markRead: boolean;
  /** In a direct message that is open, keep the newest message in view. */
  scrollDmToBottom: boolean;
  /** Work out whether the list is scrolled up, to place the "New" divider. */
  checkUnreadDivider: boolean;
  /** The message is for another channel: raise a notification. */
  notify: boolean;
  /** The key of the favourite card to scroll to the bottom, or null when the channel is not a favourite. */
  favoriteKey: string | null;
};

export function planIncoming(s: IncomingSituation): IncomingPlan {
  const isCurrentChannel = s.voidId === s.currentVoidId && s.channelId === s.currentChannelId;
  const exitContextMode = s.voidId === s.currentVoidId && s.channelId === fq(s.currentVoidId, s.currentChannelId);
  const currentFqid = fq(s.currentVoidId, s.currentChannelId);
  const msgFqid = s.channelId.includes(':') ? s.channelId : fq(s.voidId, s.channelId);
  const favKey = s.channelId.includes(':') ? s.channelId : `${s.voidId}:${s.channelId}`;
  return {
    isCurrentChannel,
    exitContextMode,
    clearOutboxFor: s.tempId ? s.tempId : null,
    markRead: exitContextMode && s.isVisible,
    scrollDmToBottom: String(s.voidId).startsWith('dm_') && isCurrentChannel,
    checkUnreadDivider: isCurrentChannel,
    notify: msgFqid !== currentFqid,
    favoriteKey: s.favorites.includes(favKey) ? favKey : null,
  };
}

/**
 * A message that is a duplicate by content (same author, same text, within 5 s) is not shown again, but it
 * must still count as unread. Returns the channel to count it against, or null when it should not count
 * (it is for the open channel, or it is the user's own message).
 */
export function planDuplicateContent(s: {
  voidId: string;
  channelId: string;
  currentVoidId: string;
  currentChannelId: string;
  authorId?: string;
  meUserId?: string;
}): string | null {
  const dedupFqid = s.channelId.includes(':') ? s.channelId : fq(s.voidId, s.channelId);
  const isMsgForCurrent = dedupFqid === fq(s.currentVoidId, s.currentChannelId);
  const isSelf = !!s.authorId && !!s.meUserId && String(s.authorId) === String(s.meUserId);
  return (!isMsgForCurrent && !isSelf) ? dedupFqid : null;
}
